import React, { useState } from 'react';
import {
  XMarkIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  SwatchIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { FormSection } from '../../types/formBuilder';
import { useLanguage } from '../../contexts/LanguageContext';
import Modal from '../common/Modal';
import { Button } from '../common/Button';

interface SectionPropertiesProps {
  isOpen: boolean;
  onClose: () => void;
  section: FormSection;
  onUpdate: (sectionId: string, updates: Partial<FormSection>) => void;
  onMoveUp?: (sectionId: string) => void;
  onMoveDown?: (sectionId: string) => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
}

const SectionProperties: React.FC<SectionPropertiesProps> = ({
  isOpen,
  onClose,
  section,
  onUpdate,
  onMoveUp,
  onMoveDown,
  canMoveUp = false,
  canMoveDown = false
}) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    label: section.label,
    description: section.description || '',
    collapsible: section.collapsible || false,
    color: section.color || 'primary'
  });

  const colorOptions = [
    // Primary & Brand Colors
    { value: 'primary', label: 'Primary', color: 'bg-primary-500' },
    { value: 'primary-light', label: 'Primary Light', color: 'bg-primary-400' },
    { value: 'primary-dark', label: 'Primary Dark', color: 'bg-primary-600' },
    
    // Blues
    { value: 'blue', label: 'Blue', color: 'bg-blue-500' },
    { value: 'blue-light', label: 'Light Blue', color: 'bg-blue-400' },
    { value: 'blue-dark', label: 'Dark Blue', color: 'bg-blue-600' },
    { value: 'sky', label: 'Sky', color: 'bg-sky-500' },
    { value: 'sky-light', label: 'Light Sky', color: 'bg-sky-400' },
    { value: 'cyan', label: 'Cyan', color: 'bg-cyan-500' },
    { value: 'cyan-light', label: 'Light Cyan', color: 'bg-cyan-400' },
    
    // Purples & Violets
    { value: 'indigo', label: 'Indigo', color: 'bg-indigo-500' },
    { value: 'indigo-light', label: 'Light Indigo', color: 'bg-indigo-400' },
    { value: 'purple', label: 'Purple', color: 'bg-purple-500' },
    { value: 'purple-light', label: 'Light Purple', color: 'bg-purple-400' },
    { value: 'violet', label: 'Violet', color: 'bg-violet-500' },
    { value: 'violet-light', label: 'Light Violet', color: 'bg-violet-400' },
    
    // Pinks & Roses
    { value: 'pink', label: 'Pink', color: 'bg-pink-500' },
    { value: 'pink-light', label: 'Light Pink', color: 'bg-pink-400' },
    { value: 'fuchsia', label: 'Fuchsia', color: 'bg-fuchsia-500' },
    { value: 'fuchsia-light', label: 'Light Fuchsia', color: 'bg-fuchsia-400' },
    { value: 'rose', label: 'Rose', color: 'bg-rose-500' },
    { value: 'rose-light', label: 'Light Rose', color: 'bg-rose-400' },
    
    // Reds & Oranges
    { value: 'red', label: 'Red', color: 'bg-red-500' },
    { value: 'red-light', label: 'Light Red', color: 'bg-red-400' },
    { value: 'red-dark', label: 'Dark Red', color: 'bg-red-600' },
    { value: 'orange', label: 'Orange', color: 'bg-orange-500' },
    { value: 'orange-light', label: 'Light Orange', color: 'bg-orange-400' },
    { value: 'orange-dark', label: 'Dark Orange', color: 'bg-orange-600' },
    
    // Yellows & Ambers
    { value: 'amber', label: 'Amber', color: 'bg-amber-500' },
    { value: 'amber-light', label: 'Light Amber', color: 'bg-amber-400' },
    { value: 'yellow', label: 'Yellow', color: 'bg-yellow-500' },
    { value: 'yellow-light', label: 'Light Yellow', color: 'bg-yellow-400' },
    
    // Greens
    { value: 'lime', label: 'Lime', color: 'bg-lime-500' },
    { value: 'lime-light', label: 'Light Lime', color: 'bg-lime-400' },
    { value: 'green', label: 'Green', color: 'bg-green-500' },
    { value: 'green-light', label: 'Light Green', color: 'bg-green-400' },
    { value: 'green-dark', label: 'Dark Green', color: 'bg-green-600' },
    { value: 'emerald', label: 'Emerald', color: 'bg-emerald-500' },
    { value: 'emerald-light', label: 'Light Emerald', color: 'bg-emerald-400' },
    { value: 'teal', label: 'Teal', color: 'bg-teal-500' },
    { value: 'teal-light', label: 'Light Teal', color: 'bg-teal-400' },
    
    // Neutrals & Grays
    { value: 'slate', label: 'Slate', color: 'bg-slate-500' },
    { value: 'slate-light', label: 'Light Slate', color: 'bg-slate-400' },
    { value: 'slate-dark', label: 'Dark Slate', color: 'bg-slate-600' },
    { value: 'gray', label: 'Gray', color: 'bg-gray-500' },
    { value: 'gray-light', label: 'Light Gray', color: 'bg-gray-400' },
    { value: 'gray-dark', label: 'Dark Gray', color: 'bg-gray-600' },
    { value: 'zinc', label: 'Zinc', color: 'bg-zinc-500' },
    { value: 'zinc-light', label: 'Light Zinc', color: 'bg-zinc-400' },
    { value: 'neutral', label: 'Neutral', color: 'bg-neutral-500' },
    { value: 'neutral-light', label: 'Light Neutral', color: 'bg-neutral-400' },
    { value: 'stone', label: 'Stone', color: 'bg-stone-500' },
    { value: 'stone-light', label: 'Light Stone', color: 'bg-stone-400' }
  ];

  const handleSave = () => {
    onUpdate(section.id, {
      label: formData.label,
      description: formData.description,
      collapsible: formData.collapsible,
      color: formData.color
    });
    onClose();
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('formBuilder.sectionProperties.title') || 'Section Properties'}
      size="4xl"
    >
      <div className="bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 min-h-full">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
          <div className="flex items-center">
            <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg mr-4">
              <SwatchIcon className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">
                {t('formBuilder.sectionProperties.title') || 'Section Properties'}
              </h2>
              <p className="text-indigo-100 text-sm mt-1">
                Customize your section appearance and behavior
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {/* Basic Properties */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4">
              <div className="flex items-center text-white">
                <div className="bg-white/20 backdrop-blur-sm p-2 rounded-lg mr-3">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold">
                  {t('formBuilder.sectionProperties.basic') || 'Basic Properties'}
                </h3>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Label */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('formBuilder.properties.label') || 'Section Label'}
                </label>
                <input
                  type="text"
                  value={formData.label}
                  onChange={(e) => handleInputChange('label', e.target.value)}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-500 text-gray-700 dark:text-gray-100 dark:bg-gray-700 transition-colors"
                  placeholder="Enter section label"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('formBuilder.properties.description') || 'Description'}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={4}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-500 text-gray-700 dark:text-gray-100 dark:bg-gray-700 transition-colors"
                  placeholder="Enter section description"
                />
              </div>

              {/* Collapsible */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="collapsible"
                    checked={formData.collapsible}
                    onChange={(e) => handleInputChange('collapsible', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="collapsible" className="ml-3 text-sm font-medium text-blue-900 dark:text-blue-100">
                    {t('formBuilder.sectionProperties.collapsible') || 'Allow section to be collapsed'}
                  </label>
                </div>
                <p className="text-xs text-blue-600 dark:text-blue-300 mt-2 ml-7">
                  Users can expand/collapse this section to save space
                </p>
              </div>
            </div>
          </div>

          {/* Color Theme */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-4">
              <div className="flex items-center text-white">
                <div className="bg-white/20 backdrop-blur-sm p-2 rounded-lg mr-3">
                  <SwatchIcon className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold">
                  {t('formBuilder.sectionProperties.appearance') || 'Color Theme'}
                </h3>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                {/* All Colors in One Grid */}
                <div>
                  <div className="grid grid-cols-12 gap-1.5">
                    {colorOptions.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleInputChange('color', option.value)}
                        className={`
                          relative w-7 h-7 rounded-lg border-2 transition-all duration-200
                          ${option.color}
                          ${formData.color === option.value 
                            ? 'border-gray-900 dark:border-gray-100 ring-2 ring-primary-500 scale-125' 
                            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 hover:scale-110'
                          }
                        `}
                        title={option.label}
                      >
                        {formData.color === option.value && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <CheckIcon className="h-3 w-3 text-white drop-shadow-lg" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section Order */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-4">
              <div className="flex items-center text-white">
                <div className="bg-white/20 backdrop-blur-sm p-2 rounded-lg mr-3">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold">
                  {t('formBuilder.sectionProperties.order') || 'Section Order'}
                </h3>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Change the position of this section within the form
                </p>
                
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    size="md"
                    onClick={() => onMoveUp && onMoveUp(section.id)}
                    disabled={!canMoveUp}
                    className="flex items-center bg-white hover:bg-green-50 border-green-300 text-green-700 disabled:opacity-50"
                  >
                    <ArrowUpIcon className="h-4 w-4 mr-2" />
                    {t('formBuilder.sectionProperties.moveUp') || 'Move Up'}
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="md"
                    onClick={() => onMoveDown && onMoveDown(section.id)}
                    disabled={!canMoveDown}
                    className="flex items-center bg-white hover:bg-green-50 border-green-300 text-green-700 disabled:opacity-50"
                  >
                    <ArrowDownIcon className="h-4 w-4 mr-2" />
                    {t('formBuilder.sectionProperties.moveDown') || 'Move Down'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions Footer */}
        <div className="flex justify-end space-x-3 p-6 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 border-t border-gray-200 dark:border-gray-600">
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
            className="bg-white hover:bg-gray-50 border-gray-300 text-gray-700 shadow-sm"
          >
            {t('common.buttons.cancel') || 'Cancel'}
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleSave}
            className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 shadow-lg transform hover:scale-105 transition-all duration-200"
          >
            {t('common.buttons.save') || 'Save Changes'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SectionProperties;